import { useState, useEffect, useCallback, useMemo } from "react";
import {
  initialCasesViewOptions,
  IRequestCases
} from "src/share/types/case/searchCase";
import useRouteState from "src/share/hooks/useRouteState";
import useDidUpdateEffect from "src/share/hooks/useDidUpdateEffect";
import { useCasesSearch } from "./useCasesSearch";
import { useCasesDateRange } from "./useCasesDateRange";
import { isFiltering } from "../utils/isFiltering";

export const useQueryOptions = casesViewType => {
  const { routeState, setRouteState } = useRouteState();
  const [isFilteringOptions, setIsFilteringOptions] = useState<boolean>(false);
  const [options, setOptions] = useState<IRequestCases>({
    ...initialCasesViewOptions,
    ...casesViewType
  });

  useEffect(() => {
    if (routeState) {
      setOptions({
        ...routeState.options,
        isRouteState: true
      });
    }
  }, []);

  useEffect(() => {
    const wasFiltered = isFiltering(casesViewType, options);
    setIsFilteringOptions(wasFiltered);
  }, [options]);

  const handleDateRangeChange = useCallback(
    date => {
      setOptions(prev => ({
        ...prev,
        page: 0,
        range: { ...date },
        isAppended: false,
        isRouteState: false
      }));
    },
    [options]
  );

  const {
    dateRange,
    showDatePicker,
    actions: {
      handleDatePickerToogle,
      handleDateFocus,
      handleDateChange,
      handleClearDateRange
    }
  } = useCasesDateRange(handleDateRangeChange);

  const handleDebounceSearchChange = (search: string) => {
    setOptions(prev => ({
      ...prev,
      search,
      page: 0,
      isAppended: false,
      isRouteState: false
    }));
  };

  const {
    searchText,
    handleClearSearchText,
    handleSearchText
  } = useCasesSearch({
    handleDebounceSearchChange
  });

  const handleClearFilters = useCallback(() => {
    handleClearSearchText();
    handleClearDateRange();
    setOptions({
      ...initialCasesViewOptions,
      ...casesViewType
    });
    setRouteState(null);
  }, [casesViewType, options]);

  useDidUpdateEffect(() => {
    handleClearFilters();
  }, [casesViewType]);

  const handleSortChange = useCallback(
    sort => {
      setOptions(prev => ({
        ...prev,
        ...casesViewType,
        page: 0,
        sortName: sort,
        isAppended: false,
        isRouteState: false,
        sort:
          sort === "missingSince"
            ? [{ missingSince: "desc" }, "fullName"]
            : [sort]
      }));
    },
    [options]
  );

  const handleLoadMore = useCallback(() => {
    setOptions(prevFilter => ({
      ...prevFilter,
      isAppended: true,
      isRouteState: false,
      page: prevFilter.page + 1
    }));
  }, [options]);

  return useMemo(
    () => ({
      dateRange,
      options,
      searchText,
      isFilteringOptions,
      showDatePicker,
      actions: {
        handleClearFilters,
        handleDateChange,
        handleDatePickerToogle,
        handleDateFocus,
        handleSortChange,
        handleLoadMore,
        handleSearchText
      }
    }),
    [
      dateRange,
      options,
      searchText,
      isFilteringOptions,
      casesViewType,
      showDatePicker,
      handleClearFilters,
      handleDateChange,
      handleDatePickerToogle,
      handleDateFocus,
      handleSortChange,
      handleLoadMore,
      handleSearchText
    ]
  );
};



// WEBPACK FOOTER //
// ./src/modules/Case/View/hooks/useQueryOptions.ts